import { useEffect } from 'react';
import { X } from 'lucide-react';
import { SideNav } from './SideNav';

/**
 * Mobile navigation drawer (2026-06-02) — the desktop left sidebar, shown on a
 * phone as a slide-in panel from a top hamburger. It renders the SAME `SideNav`
 * component (variant="drawer"), so the mobile menu is IDENTICAL to desktop and
 * can't drift — this is the congruency fix for the prior bottom-tab/`/more`
 * divergence (which also got covered by the phone browser's bottom toolbar).
 *
 * lg:hidden — desktop keeps its sticky rail. Closes on backdrop tap, Escape, or
 * tapping any nav link (onNavigate). The SideNav body only mounts while open, so
 * there's no extra nav-counts polling when the drawer is closed.
 */
export function MobileNavDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // lock background scroll while open
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <div
      className={`lg:hidden fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-slate-900/50 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      {/* Panel */}
      <div
        className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white dark:bg-slate-800 shadow-xl flex flex-col transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex items-center justify-between h-14 px-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <span className="font-headline font-semibold text-slate-900 dark:text-white">Menu</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="p-2 -mr-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          {open && <SideNav variant="drawer" onNavigate={onClose} />}
        </div>
      </div>
    </div>
  );
}
